import {
  Currency,
  DataFeedInstrumentType,
} from '../../../types/generatedTypes';

// Enums
export enum SectionId {
  INSTRUMENT_IDENTIFIERS = 'INSTRUMENT_IDENTIFIERS',
  TRANSACTION_DATE = 'TRANSACTION_DATE',
  SETTLEMENT_DATE = 'SETTLEMENT_DATE',
  SYMBOL_ISIN = 'SYMBOL_ISIN',
  BUY_SELL = 'BUY_SELL',
  QUANTITY = 'QUANTITY',
  PRICE = 'PRICE',
  BUY_QUANTITY = 'BUY_QUANTITY',
  SELL_QUANTITY = 'SELL_QUANTITY',
  COMMISSION = 'COMMISSION',
  OTHER_CHARGES = 'OTHER_CHARGES',
  ACCRUED_INTEREST = 'ACCRUED_INTEREST',
  FEE_TYPE = 'FEE_TYPE',
  DEPOSIT_WITHDRAW_TYPE = 'DEPOSIT_WITHDRAW_TYPE',
  FX_SPOT_TYPE = 'FX_SPOT_TYPE',
  DEDUCTION_REBATE = 'DEDUCTION_REBATE',
  FX_RATE = 'FX_RATE',
  SETTLEMENT_ACCOUNT = 'SETTLEMENT_ACCOUNT',
  NOTE = 'NOTE',
  BUY_CURRENCY = 'BUY_CURRENCY',
  SELL_CURRENCY = 'SELL_CURRENCY',
  CURRENCY = 'CURRENCY',
  SETTLEMENT_CURRENCY = 'SETTLEMENT_CURRENCY',
  WITHDRAW_FROM_CUSTODIAN = 'WITHDRAW_FROM_CUSTODIAN',
  DEPOSIT_TO_CUSTODIAN = 'DEPOSIT_TO_CUSTODIAN',
  EX_DIVIDEND_DATE = 'EX_DIVIDEND_DATE',
  PAYMENT_DATE = 'PAYMENT_DATE',
  AWARDED_SHARES = 'AWARDED_SHARES',
  AWARDED_CASH = 'AWARDED_CASH',
  TAX = 'TAX',
  STRIKE_PRICE = 'STRIKE_PRICE',
  EXPIRATION_DATE = 'EXPIRATION_DATE',
  OPTION_TYPE = 'OPTION_TYPE',
  UNDERLYING_SYMBOL = 'UNDERLYING_SYMBOL',
}

export enum SectionType {
  COLUMN_MAPPING = 'COLUMN_MAPPING',
  RULES = 'RULES',
  SINGLE_COLUMN = 'SINGLE_COLUMN',
  SINGLE_COLUMN_WITH_DATE = 'SINGLE_COLUMN_WITH_DATE',
  SINGLE_COLUMN_WITH_LABELS = 'SINGLE_COLUMN_WITH_LABELS',
  MATH_OPERATIONS = 'MATH_OPERATIONS',
  CURRENCY = 'CURRENCY',
  MULTI_COLUMN_RULES_WITH_CATEGORIES = 'MULTI_COLUMN_RULES_WITH_CATEGORIES',
}

export enum RuleCondition {
  OR = 'OR',
  AND = 'AND',
}

// Type groupings for combined UI tabs
// These groups share the same configuration in the UI
export const DEPOSIT_WITHDRAW_TYPES: readonly DataFeedInstrumentType[] = [
  'Deposit',
  'Withdraw',
] as const;

export const CASH_ADJUSTMENT_TYPES: readonly DataFeedInstrumentType[] = [
  'Cash Adjustment - Custodian Fee',
  'Cash Adjustment - Management Fee',
  'Cash Adjustment - Withholding Tax',
  'Cash Adjustment - Interest',
  'Cash Adjustment - Performance Fee',
  'Cash Adjustment - Misc Charge',
  'Cash Adjustment - P&L Adjustment',
  'Cash Adjustment - Income Adjustment',
] as const;

export const FX_SPOT_TYPES: readonly DataFeedInstrumentType[] = [
  'FX Spot Buy',
  'FX Spot Sell',
] as const;

// UI display types - these are what show as tabs
// Note: Some tabs represent multiple backend types
export type UIInstrumentType =
  | 'Equities'
  | 'Funds & ETFs'
  | 'FixedIncome'
  | 'Option'
  | 'Position'
  | 'FX Spot' // Represents both FX Spot Buy and FX Spot Sell
  | 'Deposit/Withdraw' // Represents both Deposit and Withdraw
  | 'Transfer'
  | 'Cash Adjustments' // Represents all 8 cash adjustment types
  | 'Dividend';

// Fee Type Categories
export enum FeeTypeCategory {
  CUSTODIAN_FEE = 'CUSTODIAN_FEE',
  INCOME_ADJUSTMENT = 'INCOME_ADJUSTMENT',
  INTEREST = 'INTEREST',
  MANAGEMENT_FEE = 'MANAGEMENT_FEE',
  MISC_CHARGE = 'MISC_CHARGE',
  PL_ADJUSTMENT = 'PL_ADJUSTMENT',
  PERFORMANCE_FEE = 'PERFORMANCE_FEE',
  WITHHOLDING_TAX = 'WITHHOLDING_TAX',
}

// Buy/Sell Categories
export enum BuySellCategory {
  BUY = 'BUY',
  SELL = 'SELL',
}

// Deposit/Withdraw Categories
export enum DepositWithdrawCategory {
  DEPOSIT = 'CASH_DEPOSIT',
  WITHDRAW = 'CASH_WITHDRAW',
}

// FX Spot Categories
export enum FxSpotCategory {
  BUY = 'BUY',
  SELL = 'SELL',
}

// Settlement Account Categories
export enum SettlementAccountCategory {
  TRANSFER = 'TRANSFER',
}

// Deduction/Rebate Categories
export enum DeductionRebateCategory {
  DEDUCTION = 'DEDUCTION',
  REBATE = 'REBATE',
}

export interface CategoryConfig {
  id:
    | FeeTypeCategory
    | BuySellCategory
    | DepositWithdrawCategory
    | FxSpotCategory
    | SettlementAccountCategory
    | DeductionRebateCategory;
  title: string;
}

// Order is determined by array position
export const FEE_TYPE_CATEGORIES: readonly CategoryConfig[] = [
  { id: FeeTypeCategory.CUSTODIAN_FEE, title: 'Custodian Fee' },
  { id: FeeTypeCategory.INCOME_ADJUSTMENT, title: 'Income Adjustment' },
  { id: FeeTypeCategory.INTEREST, title: 'Interest' },
  { id: FeeTypeCategory.MANAGEMENT_FEE, title: 'Management Fee' },
  { id: FeeTypeCategory.MISC_CHARGE, title: 'Misc Charge' },
  { id: FeeTypeCategory.PL_ADJUSTMENT, title: 'P&L Adjustment' },
  { id: FeeTypeCategory.PERFORMANCE_FEE, title: 'Performance Fee' },
  { id: FeeTypeCategory.WITHHOLDING_TAX, title: 'Withholding Tax' },
] as const;

export const BUY_SELL_CATEGORIES: readonly CategoryConfig[] = [
  { id: BuySellCategory.BUY, title: 'Buy' },
  { id: BuySellCategory.SELL, title: 'Sell' },
] as const;

export const DEPOSIT_WITHDRAW_CATEGORIES: readonly CategoryConfig[] = [
  { id: DepositWithdrawCategory.DEPOSIT, title: 'Deposit' },
  { id: DepositWithdrawCategory.WITHDRAW, title: 'Withdraw' },
] as const;

export const FX_SPOT_CATEGORIES: readonly CategoryConfig[] = [
  { id: FxSpotCategory.BUY, title: 'Buy' },
  { id: FxSpotCategory.SELL, title: 'Sell' },
] as const;

export const SETTLEMENT_ACCOUNT_CATEGORIES: readonly CategoryConfig[] = [
  { id: SettlementAccountCategory.TRANSFER, title: 'Transfer' },
] as const;

export const DEDUCTION_REBATE_CATEGORIES: readonly CategoryConfig[] = [
  { id: DeductionRebateCategory.DEDUCTION, title: 'Deduction' },
  { id: DeductionRebateCategory.REBATE, title: 'Rebate' },
] as const;

// All UI display instrument types in order (10 tabs)
export const ALL_UI_INSTRUMENT_TYPES: readonly UIInstrumentType[] = [
  'Equities',
  'Funds & ETFs',
  'FixedIncome',
  'Option',
  'FX Spot',
  'Deposit/Withdraw',
  'Transfer',
  'Cash Adjustments',
  'Dividend',
] as const;

// Helper function: Convert UI type to backend type(s)
export const getBackendTypesFromUI = (
  uiType: UIInstrumentType
): readonly DataFeedInstrumentType[] => {
  switch (uiType) {
    case 'Deposit/Withdraw':
      return DEPOSIT_WITHDRAW_TYPES;
    case 'Cash Adjustments':
      return CASH_ADJUSTMENT_TYPES;
    case 'FX Spot':
      return FX_SPOT_TYPES;
    default:
      // Direct mapping for other types
      return [uiType as DataFeedInstrumentType];
  }
};

// Helper function: Convert backend type to UI type
export const getUITypeFromBackend = (
  backendType: DataFeedInstrumentType
): UIInstrumentType => {
  if (DEPOSIT_WITHDRAW_TYPES.includes(backendType)) {
    return 'Deposit/Withdraw';
  }
  if (CASH_ADJUSTMENT_TYPES.includes(backendType)) {
    return 'Cash Adjustments';
  }
  if (FX_SPOT_TYPES.includes(backendType)) {
    return 'FX Spot';
  }
  if (backendType === 'Position') {
    return 'Position';
  }
  return backendType as UIInstrumentType;
};

// Helper function: Check if backend type matches UI type
export const isBackendTypeInUIType = (
  backendType: DataFeedInstrumentType,
  uiType: UIInstrumentType
): boolean => {
  const backendTypes = getBackendTypesFromUI(uiType);
  return backendTypes.includes(backendType);
};

// Core Interfaces
export interface Label {
  id: string;
  value: string;
}

export type RuleColumnType = 'FILE_COLUMN' | 'SECURITY_TYPE';
export type SecurityType = 'OTC' | 'Tradable';

/**
 * RuleColumn represents a single column within a rule
 * Each column has its own source column selection and labels
 */
export interface RuleColumn {
  id: string;
  columnType?: RuleColumnType;   // defaults to FILE_COLUMN (existing columns unaffected)
  sourceColumn: string; // The selected column from available headers
  labels: Label[]; // Labels associated with this column
  securityType?: SecurityType;   // only used when columnType = SECURITY_TYPE
}

/**
 * Rule with inline column selection
 * Each rule contains its own columns instead of relying on external column mappings
 */
export interface Rule {
  id: string;
  name: string;
  columns: RuleColumn[]; // Array of columns with their own source and labels
  conditions: RuleCondition[]; // Array of conditions between columns (n-1 for n columns)
}

// Category Rules Structure
export interface CategoryRuleSection {
  categoryId:
    | FeeTypeCategory
    | BuySellCategory
    | DepositWithdrawCategory
    | FxSpotCategory
    | SettlementAccountCategory
    | DeductionRebateCategory;
  categoryName: string;
  rules: Rule[];
}

export interface ColumnMapping {
  id: string;
  sourceColumn: string;
}

export interface SignDetectionConfig {
  sourceColumn: string;
  negativeCategory: string;
  positiveCategory: string;
}

export interface SectionConfig {
  columnMappings?: ColumnMapping[];
  rules?: Rule[];
  categoryRules?: CategoryRuleSection[];
  simpleValue?: string;
  dateFormat?: string;
  labels?: Label[];
  buySellLabels?: {
    buy: Label[];
    sell: Label[];
  };
  mathOperation?: {
    formula: string;
    variables: { [key: string]: string };
  };
  currencyMappings?: {
    currency: Currency;
    variations: string[];
  }[];
  signDetection?: SignDetectionConfig;
  // COMMENTED OUT: Holdings lookup functionality disabled
  // holdingsLookup?: {
  //   enabled: boolean;
  //   symbolColumn: string | null;
  // };
}

export interface ConfigurationSection {
  id: string;
  title: string;
  type: SectionType;
  config: SectionConfig;
}

export interface InstrumentConfig {
  instrumentType: UIInstrumentType;
  sections: ConfigurationSection[];
}

export interface ConfigurationTemplate {
  id: string;
  name: string;
  instrumentConfigs: InstrumentConfig[];
}

// Legacy interfaces (to be removed in later phases)
export interface TabConfig {
  category: string;
  headers: string[];
}

export interface ConfigurationProps {
  loading?: boolean;
  initialTemplateId?: string | null;
  isVisible?: boolean;
}

/**
 * Generate rules section ID for a given section
 * Example: getRulesSectionId(SectionId.INSTRUMENT_IDENTIFIERS) -> "INSTRUMENT_IDENTIFIERS_RULES"
 *
 * Note: INSTRUMENT_IDENTIFIERS uses inline rules (stored in section.config.rules).
 * This function is for other sections that use separate rules sections.
 */
export const getRulesSectionId = (sectionId: SectionId): string => {
  return `${sectionId}_RULES`;
};
